/**
 * Norwegian vacation calendar for the usage views. July (fellesferie),
 * Easter week and Christmas empty the offices, so activity charts dip and
 * 28d-vs-previous trends read as churn when they are really out-of-office.
 * The charts shade these spans and the trend surfaces flag comparisons that
 * overlap them — the signal is "seasonal, judge accordingly", never a data
 * correction.
 */

import { ONE_DAY_IN_MS } from "@/v2-backoffice-client/constants"

export type HolidaySpan = {
  key: string
  label: string
  from: Date
  to: Date
}

/** Anonymous Gregorian (Gauss/Meeus) computus — exact for all years. */
function easterSunday(year: number): Date {
  const a = year % 19
  const b = Math.floor(year / 100)
  const c = year % 100
  const d = Math.floor(b / 4)
  const e = b % 4
  const f = Math.floor((b + 8) / 25)
  const g = Math.floor((b - f + 1) / 3)
  const h = (19 * a + b - d - g + 15) % 30
  const i = Math.floor(c / 4)
  const k = c % 4
  const l = (32 + 2 * e + 2 * i - h - k) % 7
  const m = Math.floor((a + 11 * h + 22 * l) / 451)
  const month = Math.floor((h + l - 7 * m + 114) / 31)
  const day = ((h + l - 7 * m + 114) % 31) + 1
  return new Date(year, month - 1, day)
}

function spansForYear(year: number): HolidaySpan[] {
  const easter = easterSunday(year)
  return [
    {
      key: `easter-${year}`,
      label: "Påske",
      // Palm weekend through Easter Monday — the week Norway is away.
      from: new Date(easter.getTime() - 8 * ONE_DAY_IN_MS),
      to: new Date(easter.getTime() + 1 * ONE_DAY_IN_MS),
    },
    {
      key: `july-${year}`,
      label: "Fellesferie",
      from: new Date(year, 6, 1),
      to: new Date(year, 6, 31),
    },
    {
      key: `christmas-${year}`,
      label: "Jul",
      from: new Date(year, 11, 20),
      to: new Date(year + 1, 0, 2),
    },
  ]
}

/** All vacation spans intersecting [from, to], in calendar order. */
export function getHolidaySpans(from: Date, to: Date): HolidaySpan[] {
  const spans: HolidaySpan[] = []
  for (let year = from.getFullYear() - 1; year <= to.getFullYear(); year += 1) {
    for (const span of spansForYear(year)) {
      if (span.to >= from && span.from <= to) spans.push(span)
    }
  }
  return spans.sort((a, b) => a.from.getTime() - b.from.getTime())
}

/**
 * Note for trend pills whose comparison windows (current + previous, i.e.
 * the 2×`windowDays` up to `end`) overlap a vacation span — null when the
 * trend is clean.
 */
export function describeTrendSeasonNote(
  end: Date,
  windowDays = 28,
): string | null {
  const spans = getHolidaySpans(
    new Date(end.getTime() - 2 * windowDays * ONE_DAY_IN_MS),
    end,
  )
  if (spans.length === 0) return null
  const labels = [...new Set(spans.map((span) => span.label))]
  return `Trend windows overlap ${labels.join(" and ")} — vacation lulls read as drops, judge accordingly.`
}
